import jwt_decode from 'jwt-decode'
import _ from 'lodash'

import {
  identifyUserForAnalytics,
  resetUserForAnalytics,
  track,
} from 'pared/analytics/user'
import { FE2_BASE_URL } from 'pared/constants'
import { FE2_CUSTOMER_CODE } from 'pared/constants/brands'
import getUserByEmail, {
  IUser,
  USER_INFO_VERSION,
} from 'pared/data/getUserByEmail'
import { decodedUserJwtToken, userProfile } from 'pared/reactiveVariables/user'
import { getBrand } from 'pared/utils/brand'

interface IUserWithJwt extends IUser {
  jwt: string
}

const FE2_CUSTOMER_CODE_KEY = 'fe2CustomerCode'
const FE2_SEED_FAILED_KEY = 'fe2SeedFailed'

export function getFe2CustomerCode(brand: string) {
  // why: the raw env brand code keeps the _staging__ prefix that expo's DCM
  // routing needs; fall back to the brand itself for codes not in the map.
  return FE2_CUSTOMER_CODE[brand] || brand
}

export function hasFe2SeedFailed() {
  return localStorage.getItem(FE2_SEED_FAILED_KEY) === '1'
}

export function getSeededFe2CustomerCode() {
  // why: refresh, sign-out, and the FE V2 iframe must all address the same
  // customer code the session was seeded with; deriving from the current
  // brand is only the fallback for sessions that predate persistence (their
  // one forced re-sign-in stores it).
  return (
    localStorage.getItem(FE2_CUSTOMER_CODE_KEY) ||
    getFe2CustomerCode(getBrand())
  )
}

export function recordFe2SessionSeed(
  fe2CustomerCode: string,
  succeeded: boolean,
) {
  // why: syncFe2Session must refresh against the customer code the session
  // was actually seeded with (not whatever getBrand() returns at app boot),
  // and must know when the seed failed so it does not turn that failure into
  // a V1 sign-out loop.
  localStorage.setItem(FE2_CUSTOMER_CODE_KEY, fe2CustomerCode)
  if (succeeded) {
    localStorage.removeItem(FE2_SEED_FAILED_KEY)
  } else {
    localStorage.setItem(FE2_SEED_FAILED_KEY, '1')
  }
}

export async function signInUser(jwtToken: string, email: string) {
  const user = await getUserByEmail(email)
  if (user.isDeactivated) {
    throw 'User has been deactivated'
  }

  if (user && user.userId) {
    await identifyUserForAnalytics(user)
    await track.signIn()
    localStorage.setItem('userJwtToken', jwtToken)
    localStorage.setItem('user', JSON.stringify(user))
    userProfile(user)
    decodedUserJwtToken(jwt_decode(jwtToken))
  }
}

export async function syncFe2Session() {
  // why: this runs from module scope (loadUserPersistedData), before the
  // brand store is populated, so getBrand() would always resolve to
  // BRAND_CODES[0] - refresh against the customer code the session was
  // actually seeded with.
  const fe2CustomerCode = getSeededFe2CustomerCode()

  try {
    const res = await fetch(
      `${FE2_BASE_URL}/api/${fe2CustomerCode}/iframe-auth/refresh`,
      {
        method: 'POST',
        // why: the FE2.0 session cookie must ride along - the endpoint
        // extends its expiry when it exists.
        credentials: 'include',
      },
    )

    if (res.ok) {
      localStorage.removeItem(FE2_SEED_FAILED_KEY)
      return
    }

    // why: only a definitive 401 means the FE2.0 session is gone - sign the
    // user out of V1 so the next sign-in reseeds both sessions. Network or
    // server errors must NOT sign the user out, or an FE2.0 outage would
    // boot every V1 user. And when the seed itself is known to have failed,
    // signing out would loop (sign in -> failed seed -> 401 -> sign out ->
    // ...), so keep the user in V1 and leave the iframes dark instead.
    if (
      res.status === 401 &&
      localStorage.getItem(FE2_SEED_FAILED_KEY) !== '1'
    ) {
      await signOutUser()
    }
  } catch {
    // why: FE2.0 being unreachable must not break V1.
  }
}

export async function signOutUser() {
  const fe2CustomerCode = getSeededFe2CustomerCode()

  if (!decodedUserJwtToken() || !_.isEqual(userProfile(), {})) {
    await fetch(`${FE2_BASE_URL}/api/${fe2CustomerCode}/iframe-auth/sign-out`, {
      method: 'POST',
      // why: the session cookie must ride along or signOut() on the expo side
      // destroys an empty session and the user stays signed in to the iframe.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }).catch(() => {
      // why: a failed FE2.0 sign-out must not block V1 sign-out - the local
      // session cleanup below has to run regardless.
    })
    decodedUserJwtToken(null)
    userProfile({})
    localStorage.setItem('userJwtToken', '')
    localStorage.setItem('user', '')
    localStorage.removeItem(FE2_CUSTOMER_CODE_KEY)
    localStorage.removeItem(FE2_SEED_FAILED_KEY)

    await track.signOut()
    await resetUserForAnalytics()
  }
}

export function getUserPersistedJwtToken() {
  const jwtToken = localStorage.getItem('userJwtToken')
  return jwtToken
}

export function getUser() {
  const user: IUserWithJwt = JSON.parse(localStorage.getItem('user') || '{}')
  user.jwt = localStorage.getItem('userJwtToken') || ''
  return user
}

export async function loadUserPersistedData() {
  const jwtToken = getUserPersistedJwtToken()
  if (jwtToken) {
    decodedUserJwtToken(jwt_decode(jwtToken))
  } else {
    decodedUserJwtToken(null)
  }

  const user = getUser()

  // make sure the user has userId and has the correct version, otherwise sign the user out
  if (user && user.userId && user.version === USER_INFO_VERSION) {
    userProfile(user)
    await identifyUserForAnalytics(user)
    // why: FE V2 iframes need a live FE2.0 session. On every app load,
    // extend it if it exists, or force a V1 sign-out (via the 401 handling
    // inside) when it is gone - covers users who signed in before FE2.0
    // shipped and sessions that lapsed while the V1 jwt was still valid.
    // Fire-and-forget so it never delays app boot.
    syncFe2Session()
  } else {
    await signOutUser()
  }
}

export function hasPrivilegeToViewEmployeeFeedback() {
  const user = getUser()
  const decodedJwt = decodedUserJwtToken()

  if (
    decodedJwt &&
    (decodedJwt.role === 'expo_user' || decodedJwt.role === 'postgres') &&
    user.isDrgEmployeeFeedbackViewable === true
  ) {
    return true
  }
  return false
}
